import React, { FC, useMemo } from 'react';
import { TimeInput } from 'src/components/general/TimeInput';
import { timeToSeconds } from 'src/utils/DateTimeUtils';
import { AssetConstants } from 'src/constants';
import styles from './VideoDurationInput.module.css';

interface VideoDurationInputProps {
  startTime: string;
  endTime: string;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  videoDuration?: number;
  disabled?: boolean;
  minDuration?: number; // Default: 5
  maxDuration?: number; // Default: 21
  'data-testid': string;
}

const validateTimeOrder = (start: number, end: number): string => {
  return start < end ? '' : 'End time must be after start time';
};

const validateEndTimeWithinVideo = (end: number, videoDuration?: number): string => {
  if (videoDuration && end > Math.round(videoDuration)) {
    return `End time cannot exceed video duration (${Math.round(videoDuration)}s)`;
  }
  return '';
};

const validateDurationConstraints = (
  duration: number,
  min: number,
  max: number,
  videoDuration?: number,
): string => {
  if (videoDuration && videoDuration < min) {
    return `Video is too short (${Math.floor(videoDuration)}s). Minimum required: ${min}s`;
  } else if (duration < min) {
    return `Duration must be at least ${min} seconds`;
  } else if (duration > max) {
    return `Duration must not exceed ${max} seconds (current: ${duration}s)`;
  }
  return '';
};

/**
 * VideoDurationInput component for selecting video start and end times
 * Validates duration constraints
 */
export const VideoDurationInput: FC<VideoDurationInputProps> = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  videoDuration,
  disabled = false,
  minDuration = AssetConstants.VIDEO_DURATION.MIN_DURATION,
  maxDuration = AssetConstants.VIDEO_DURATION.MAX_DURATION,
  'data-testid': dataTestId,
}) => {
  const { startError, endError, durationError } = useMemo(() => {
    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);
    const duration = endSeconds - startSeconds;

    // Only validate if both times are provided
    if (!startTime || !endTime) {
      return { startError: '', endError: '', durationError: '' };
    }

    const timeOrderError = validateTimeOrder(startSeconds, endSeconds);
    const endTimeError = timeOrderError || validateEndTimeWithinVideo(endSeconds, videoDuration);
    const durationConstraintError = validateDurationConstraints(
      duration,
      minDuration,
      maxDuration,
      videoDuration,
    );

    return {
      startError: '',
      endError: endTimeError,
      durationError: durationConstraintError,
    };
  }, [startTime, endTime, videoDuration, minDuration, maxDuration]);

  const displayDuration = useMemo(() => {
    if (!startTime || !endTime) return '';
    const duration = timeToSeconds(endTime) - timeToSeconds(startTime);
    return duration > 0 ? `${duration}s` : '';
  }, [startTime, endTime]);

  return (
    <div className={styles.container} data-testid={dataTestId}>
      <div className={styles.timeInputs}>
        <TimeInput
          label="Start Time"
          value={startTime}
          onChange={onStartTimeChange}
          error={startError}
          disabled={disabled}
          data-testid={`${dataTestId}-start-time`}
        />
        <TimeInput
          label="End Time"
          value={endTime}
          onChange={onEndTimeChange}
          error={endError}
          disabled={disabled}
          data-testid={`${dataTestId}-end-time`}
        />
      </div>

      {(durationError || displayDuration) && (
        <div
          className={durationError ? styles.durationError : styles.durationInfo}
          data-testid={`${dataTestId}-duration-${durationError ? 'error' : 'info'}`}
        >
          {durationError || `Duration: ${displayDuration}`}
        </div>
      )}
    </div>
  );
};
